<script setup lang="ts">
import '../assets/style.scss'
import type { PropType } from 'vue'
import { computed } from 'vue'
import { useTranslate } from '@/shared/lib'
import { useMandatoryFieldError } from '../../model/useMandatoryFieldError'
import { resolveTextFieldMaxLength } from '../../model/resolveTextFieldMaxLength'

const props = defineProps({
  type: {
    type: String as PropType<'string' | 'phone' | 'email' | 'link' | 'link_file'>,
    default: 'string',
  },
  hideDetails: {
    type: Boolean,
    default: true,
  },
  label: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  length: {
    type: Number,
    default: 1000000000000,
  },
  mandatory: {
    default: false,
  },
  showMandatoryError: {
    type: Boolean,
    default: false,
  },
})

const value = defineModel<string>({ default: '' })
const { trs } = useTranslate()

/** Catalog may store `0` / `"0"` for «без ограничения»; only positive limits truncate input. */
const effectiveMaxLength = computed(() => resolveTextFieldMaxLength(props.length))
const {
  visible: mandatoryErrorVisible,
  message: mandatoryErrorMessage,
  markTouched,
  registerUserInteraction,
  touched: mandatoryTouched,
} = useMandatoryFieldError(
  () => ({
    showMandatoryError: props.showMandatoryError,
    mandatory: props.mandatory,
    type: props.type,
  }),
  () => value.value,
)

/**
 * Правила валидации
 */
const types: { prefix: string; valid: RegExp } = {
  string: {
    prefix: '',
    valid: /./,
  },
  phone: {
    prefix: '+7',
    valid: /^(\+7|7|8)?[\s\-]?\(?[489][0-9]{2}\)?[\s\-]?[0-9]{3}[\s\-]?[0-9]{2}[\s\-]?[0-9]{2}$/,
  },
  email: {
    prefix: '',
    valid: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  },
  link: {
    prefix: 'https://',
    valid:
      /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/,
  },
  link_file: {
    prefix: 'file://',
    valid:
      /^file?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/,
  },
}

/**
 * Формат (email, phone, …) — только после blur и только если есть ввод.
 */
const validateFormat = (value: string) => {
  if (!value || value === types[props.type].prefix) return true
  if (types[props.type].valid.test(value)) return true
  return trs(`validate.field_validate.${props.type}`)
}

const showFormatRules = computed(
  () => mandatoryTouched.value && Boolean(String(value.value ?? '').trim()),
)

/**
 * Функция обработки фокуса
 */
const focusHandler = () => {
  if (value.value) return

  value.value = types[props.type].prefix
}

/**
 * Функция обработки потери фокуса
 */
const blurHandler = () => {
  markTouched()
  if (!value.value) return
  if (value.value == types[props.type].prefix) {
    value.value = ''
  }
}

/**
 * Функция обработки ввода
 */
const inputHandler = () => {
  const max = effectiveMaxLength.value
  if (max != null && value.value.length > max) {
    value.value = value.value.slice(0, max)
  }
}
</script>

<template>
  <VTextField
    v-model="value"
    validate-on="blur"
    :rules="showFormatRules ? [validateFormat] : []"
    :hide-details="mandatoryErrorVisible ? false : hideDetails"
    :label="label"
    :disabled="disabled"
    :loading="loading"
    :error="mandatoryErrorVisible ? true : undefined"
    :error-messages="mandatoryErrorVisible ? mandatoryErrorMessage : undefined"
    @pointerdown="registerUserInteraction()"
    @focus="focusHandler"
    @blur="blurHandler"
    @input="inputHandler"
  />
</template>
